import React, { useMemo } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { useFlexLayout, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import { getDisplayStringFromEnum } from '../../../../enums/wallet-brand-name';
import useSellerLabelProviderBalanceMap from '../../../settings/providers/providers-list/hooks/use-seller-label-provider-balance-map';
import { useSellerLabelProviders } from '../../../../hooks/use-seller-label-providers';
import { useSellerWallets } from '../../../../hooks/use-seller-wallets';
import Global from '../../../../store/global-selectors';
import FormattedCurrency from '../../../../components/formatted-currency';
import { doubleDash } from '../../constants';
import commonStyle from '../common-styles.module.less';
import tableStyle from './style.module.less';

const TITLE = 'title';
const NAME = 'name';
const BALANCE = 'balance';

const messages = defineMessages({
  [TITLE]: {
    defaultMessage: 'Current Account Balance',
    id: 'y95/jO',
    description: 'current account balance table header',
  },
  [NAME]: {
    defaultMessage: 'Name',
    id: 'NZ+0oN',
    description: 'current account balance name header',
  },
  [BALANCE]: {
    defaultMessage: 'Balance',
    id: '/XjAvl',
    description: 'current account balance balance header',
  },
});

type BalanceRow = {
  name: string;
  balance: number | undefined;
  currencyCode: string;
};

export const CurrentAccountBalanceTable = () => {
  const intl = useIntl();

  const { balancesMap } = useSellerLabelProviderBalanceMap();
  const sellerLabelProviders = useSellerLabelProviders();
  const wallets = useSellerWallets();
  const labelProviders = useSelector(Global.selectReferenceDatagetProviders);

  const filteredBalancesMap = useMemo(() => {
    const allWalletSellerLabelProviderIds = wallets.flatMap(
      (wallet: { resources?: { sellerLabelProviderIds?: number[] } }) => wallet?.resources?.sellerLabelProviderIds ?? [],
    );

    const filteredBalanceMap = allWalletSellerLabelProviderIds.reduce(
      (acc: Record<string, unknown>, slpId: number) => {
        const { [slpId]: _removed, ...updatedAccumulator } = acc;
        return updatedAccumulator;
      },
      { ...balancesMap },
    );
    return filteredBalanceMap;
  }, [wallets, balancesMap]);

  const tableTitle = useMemo(() => intl.formatMessage(messages[TITLE]), [intl]);

  const columns = useMemo(
    () => [
      {
        Header: intl.formatMessage(messages[NAME]),
        accessor: NAME as typeof NAME,
      },
      {
        Header: () => <div className={commonStyle.right}>{intl.formatMessage(messages[BALANCE])}</div>,
        accessor: BALANCE as typeof BALANCE,
        Cell: ({ value, row }: { value: number | undefined; row: { original: BalanceRow } }) => (
          <div
            className={classnames(commonStyle.right, {
              [tableStyle.negativeValue]: value !== undefined && value <= 0,
            })}
          >
            {value === undefined ? (
              doubleDash
            ) : (
              <>
                <FormattedCurrency currencyCode={row.original.currencyCode} value={value} /> {row.original.currencyCode}
              </>
            )}
          </div>
        ),
      },
    ],
    [intl],
  );

  const tableData = useMemo(() => {
    return [
      ...wallets.map(
        (wallet: { walletId: string; nickname?: string; brand: string; balance?: { value: number }; currencyCode: string }) => ({
          name: wallet.nickname ? `${getDisplayStringFromEnum(wallet.brand)} - ${wallet.nickname}` : getDisplayStringFromEnum(wallet.brand),
          balance: wallet?.balance?.value,
          currencyCode: wallet.currencyCode,
        }),
      ),
      ...Object.keys(filteredBalancesMap)
        .filter((slpId) => {
          const providerBalance = balancesMap[slpId];
          return sellerLabelProviders.some((slp) => slp.sellerLabelProviderId === providerBalance.sellerLabelProviderId);
        })
        .map((slpId) => {
          const providerBalance = balancesMap[slpId];
          const matchingSellerLabelProvider = sellerLabelProviders.find(
            (slp) => slp.sellerLabelProviderId === providerBalance.sellerLabelProviderId,
          );
          const matchingLabelProvider = labelProviders.find(
            (lp: { labelProviderId: string }) => lp.labelProviderId === matchingSellerLabelProvider?.labelProviderId,
          );
          return {
            name:
              matchingLabelProvider && matchingSellerLabelProvider?.name
                ? `${matchingLabelProvider.name} - ${matchingSellerLabelProvider.name}`
                : matchingLabelProvider?.name,
            balance: providerBalance?.balance?.value,
            currencyCode: providerBalance?.balance?.code,
          };
        }),
    ];
  }, [wallets, balancesMap, filteredBalancesMap, labelProviders, sellerLabelProviders]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<BalanceRow>(
    {
      columns,
      data: tableData,
    },
    useFlexLayout,
  );

  return (
    <>
      <div className={commonStyle['table-title']}>
        <Text textType={TextType.H3} value={tableTitle} />
      </div>
      <ReactTableWrapper
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
      />
    </>
  );
};
